import { breakpoints, Button, Dialog, Inline, Stack, Text } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import type {
  BoundNew,
  BrandedFare,
  PaymentMethodPrice,
  TravelerNew,
  TravelerPrice,
} from '@eti/schema-types';
import { price as priceUtil } from '@eti/utils';
import { css } from '@eti/styles';
import sumBy from 'lodash/sumBy';
import CartToggleableRow from '../../../../widgets/cart/components/CartToggleableRow';
import CartPriceSummary from '../../../../widgets/cart/components/CartPriceSummary';
import { getMarketingCarrierNamesToDisplayInTaxBreakdown } from '../../../../utils/carriers';
import { getTravelerPrices } from '../../../../utils/travelers';
import {
  CartRow,
  CartSubSectionTitle,
  Price,
} from '../../../../widgets/cart/components/CartComponents';
import { getPaymentMethodPrices, getProductsTextAndTotalPrice } from '../v3/utils';
import type { AvailableExtraProductsType } from '../v3/branded-fares/model';
import { useCheapestPaymentMethod } from '../../../../utils/paymentMethods';
import type { AncillaryBundleOptionType } from '../../../../common/utils/models';
import PriceBreakdownTravelerGroup from './PriceBreakdownTravelerGroup';

const paymentMethodsTitleStyle = css`
  margin-bottom: 12px;
`;

interface TextandTotalPrice {
  id: number;
  text: string;
  totalPrice: number;
}

interface PriceInformationModalProps {
  availableExtraProducts?: AvailableExtraProductsType[];
  bounds: BoundNew[];
  discountAmount: number;
  finalProductIdsToPurchase?: number[];
  isOpen: boolean;
  handleClose: () => void;
  paymentMethodPrices: PaymentMethodPrice[];
  selectedBundleOption?: AncillaryBundleOptionType;
  selectedFare?: BrandedFare;
  travelerPrices: TravelerPrice[];
  travelerPricesWithoutDiscounts: TravelerPrice[];
  tripTravelers: TravelerNew[];
}

export const PriceInformationModal = ({
  availableExtraProducts: bundleTripAvailableExtraProducts,
  bounds,
  discountAmount,
  finalProductIdsToPurchase = [],
  isOpen,
  handleClose,
  paymentMethodPrices = [],
  selectedBundleOption,
  selectedFare,
  travelerPrices,
  travelerPricesWithoutDiscounts,
  tripTravelers,
}: PriceInformationModalProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();

  const extraProductsText = {
    cfarTicketTotal: t('Product.CancelForAnyReasonTicket.PriceSpecification.Total'),
    checkedBaggageTotal: t('Product.CheckedBaggage.PriceSpecification.Total'),
    climateCompensationTotal: t('Product.ClimateCompensation.PriceSpecification.Total'),
    flexibleTicketTotal: t('Product.FlexibleTicket.PriceSpecification.Total'),
  };

  const paymentMethods = getPaymentMethodPrices(paymentMethodPrices);
  const selectedPaymentMethod = useCheapestPaymentMethod(paymentMethods);

  const zeroTaxesCarriers = p('BookingFlow.HideTaxIfZero.Carriers');
  const carriersToDisplayInTaxBreakdown = p('BookingFlow.SupplierCostMarkupBreakdown.Carriers');
  const marketingCarrierNames = getMarketingCarrierNamesToDisplayInTaxBreakdown(
    bounds,
    carriersToDisplayInTaxBreakdown,
    zeroTaxesCarriers,
  );

  const updatedTravelerPrices = selectedFare?.travelerPrices || travelerPrices;
  const updatedTripTravelers = selectedFare?.travelers || tripTravelers;

  const updatedPrices = getTravelerPrices(updatedTripTravelers, updatedTravelerPrices);
  const updatedPricesWithoutDiscount = getTravelerPrices(
    updatedTripTravelers,
    travelerPricesWithoutDiscounts,
  );

  const isProductsAvialableToPurchase = finalProductIdsToPurchase.length > 0;

  let productsTextAndTotalPrice = [] as TextandTotalPrice[];
  let totalExtraProductsPrice = 0;

  if (isProductsAvialableToPurchase) {
    const availableExtraProducts =
      selectedFare?.availableExtraProducts || bundleTripAvailableExtraProducts;
    productsTextAndTotalPrice = getProductsTextAndTotalPrice(
      availableExtraProducts as AvailableExtraProductsType[],
      extraProductsText,
      finalProductIdsToPurchase,
    ) as TextandTotalPrice[];
    totalExtraProductsPrice = sumBy(productsTextAndTotalPrice, 'totalPrice');
  }

  const etgFaresPrice = selectedBundleOption?.priceRaw ?? 0;
  const etgFaresPriceWithoutDiscount = selectedBundleOption?.originalPrice ?? 0;
  const farePrice = sumBy(updatedPrices, 'totalPriceSum');
  const farePriceWithoutDiscount = sumBy(updatedPricesWithoutDiscount, 'totalPriceSum');
  const subTotalPrice =
    farePriceWithoutDiscount + etgFaresPriceWithoutDiscount + totalExtraProductsPrice;
  const totalFarePrice = farePrice + etgFaresPrice + totalExtraProductsPrice - discountAmount;

  const shouldDisplayExtraServices =
    (isProductsAvialableToPurchase && productsTextAndTotalPrice?.length > 0) ||
    selectedBundleOption;

  return (
    <Dialog
      closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
      footer={
        <Inline align="end" collapseBelow={breakpoints._768} spacing={8}>
          <Button data-testid="close-dialog-button" onClick={handleClose} variant="primary">
            {t('Close')}
          </Button>
        </Inline>
      }
      isOpen={isOpen}
      onDismiss={handleClose}
      title={t('TripDetails.AirSupplierCostSpecification.Header')}
    >
      <Stack role="main">
        {updatedPricesWithoutDiscount.map(
          ({ ageType, markup, price, totalPriceSum, quantity, vat }) => (
            <PriceBreakdownTravelerGroup
              ageType={ageType}
              key={ageType}
              marketingCarrierNames={marketingCarrierNames}
              markup={markup}
              price={price}
              quantity={quantity}
              totalPrice={totalPriceSum}
              vat={vat}
            />
          ),
        )}
        <div data-testid="trip-priceBreakdown-extraServices">
          {shouldDisplayExtraServices && (
            <CartRow data-testid="trip-priceBreakdown-extraServices">
              <CartSubSectionTitle data-testid="trip-priceBreakdown-extraServices-label">
                {t('Cart.ExtraServices.Label')}
              </CartSubSectionTitle>
            </CartRow>
          )}
          {selectedBundleOption && (
            <CartToggleableRow
              data={selectedBundleOption.features}
              dataTestId="trip-priceBreakdown-EtgFares-option"
              price={selectedBundleOption.priceRaw}
              title={selectedBundleOption.title}
            />
          )}
          {isProductsAvialableToPurchase &&
            productsTextAndTotalPrice?.map(({ id, text, totalPrice }) => {
              return (
                <CartRow key={text}>
                  <Text data-testid={`trip-priceBreakdown-${id}-label`}>{text}</Text>
                  <Price data-testid={`trip-priceBreakdown-${id}-price`}>
                    {priceUtil.formatPrice(totalPrice)}
                  </Price>
                </CartRow>
              );
            })}
        </div>
        <CartPriceSummary
          dataTestId="trip-priceBreakdown-summary"
          discountAmount={discountAmount}
          // @ts-ignore - WEBTECH-1361
          paymentMethods={paymentMethods}
          selectedPaymentMethod={selectedPaymentMethod}
          shouldShowPaymentOptions={false}
          subTotalPrice={subTotalPrice}
          totalPrice={totalFarePrice}
        />
        <CartToggleableRow
          data={paymentMethods}
          dataTestId="trip-priceBreakdown-paymentMethods"
          title={t('Cart.PaymentFees.Label')}
          titleStyle={paymentMethodsTitleStyle}
        />
      </Stack>
    </Dialog>
  );
};
